import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { CONFLUENCE_GENERATOR_ID, JIRA_GENERATOR_ID } from '../../constants';
import extractDocumentTypeIcon from './extract-document-type-icon';
import extractFileFormatIcon from './extract-file-formatIcon';
import extractJiraTaskIcon from './extract-jira-task-icon';
import extractProviderIcon from './extract-provider-icon';
import extractUrlIcon from './extract-url-icon';
import { extractTaskType } from '../../common/lozenge';
import { extractTitle } from '@atlaskit/link-extractors';
import { extractorPriorityMap as priorityMap } from '../../common/icon/priority';
import { IconType } from '../../../constants';
import { prioritiseIcon } from '../../common/icon';
const extractTask = (data, label) => {
  const {
    id,
    icon: url
  } = extractTaskType(data) || {};
  const taskType = id === null || id === void 0 ? void 0 : id.split('#').pop();
  const taskIcon = url ? {
    label,
    url
  } : undefined;
  return {
    taskType,
    taskIcon
  };
};
const extractType = jsonLd => {
  const type = jsonLd['@type'];
  return Array.isArray(type) ? type.sort((a, b) => priorityMap[b] - priorityMap[a])[0] : type;
};
const isConfluenceProvider = provider => provider === CONFLUENCE_GENERATOR_ID;
const isJiraProvider = provider => provider === JIRA_GENERATOR_ID;
const isNativeProvider = provider => isConfluenceProvider(provider) || isJiraProvider(provider);

/**
 * Return the icon object given a JSON-LD data object.
 */
const extractJsonldDataIcon = data => {
  const generator = data.generator;
  const label = extractTitle(data);
  const type = extractType(data);
  const urlIcon = extractUrlIcon(data.icon, label);
  const provider = generator === null || generator === void 0 ? void 0 : generator['@id'];
  const providerIcon = extractProviderIcon(data);
  switch (type) {
    case 'Document':
    case 'schema:BlogPosting':
    case 'schema:DigitalDocument':
    case 'schema:TextDigitalDocument':
    case 'schema:PresentationDigitalDocument':
    case 'schema:SpreadsheetDigitalDocument':
    case 'atlassian:Template':
    case 'atlassian:UndefinedLink':
      // If we encounter a native provider, we privilege the
      // icon generated by its mime type and document type.
      // Otherwise, we privilege the provider icon,
      // followed by mime type and document type icon.
      const documentData = data;
      const fileFormat = documentData === null || documentData === void 0 ? void 0 : documentData['schema:fileFormat'];
      const fileFormatIcon = extractFileFormatIcon(fileFormat);
      const documentTypeIcon = extractDocumentTypeIcon(type, label);
      if (getBooleanFF('platform.linking-platform.smart-card.standardise-smart-link-icon-behaviour')) {
        return prioritiseIcon({
          providerId: provider,
          fileFormatIcon,
          documentTypeIcon,
          urlIcon,
          providerIcon
        });
      }
      return isNativeProvider(provider) ? fileFormatIcon || documentTypeIcon || urlIcon || providerIcon : providerIcon || fileFormatIcon || documentTypeIcon;
    case 'atlassian:Task':
      const taskLabel = label || 'Task';
      if (isJiraProvider(provider)) {
        const {
          taskType,
          taskIcon
        } = extractTask(data);
        return taskType === 'JiraCustomTaskType' ? taskIcon || urlIcon || providerIcon || {
          icon: IconType.Task,
          label: taskLabel
        } : extractJiraTaskIcon(taskType, taskLabel);
      }
      return {
        icon: IconType.Task,
        label: taskLabel
      };
    case 'atlassian:Goal':
      return urlIcon || {
        icon: IconType.Task,
        label: label || 'Goal'
      };
    case 'atlassian:Project':
      return urlIcon || {
        icon: IconType.Project,
        label: label || 'Project'
      };
    case 'atlassian:SourceCodeCommit':
      return {
        icon: IconType.Commit,
        label: label || 'Commit'
      };
    case 'atlassian:SourceCodePullRequest':
      return {
        icon: IconType.PullRequest,
        label: label || 'Pull request'
      };
    case 'atlassian:SourceCodeReference':
      return {
        icon: IconType.Branch,
        label: label || 'Reference'
      };
    case 'atlassian:SourceCodeRepository':
      return {
        icon: IconType.Repo,
        label: label || 'Repository'
      };
    default:
      return getBooleanFF('platform.linking-platform.smart-card.standardise-smart-link-icon-behaviour') ? extractDefaultIcon({
        data,
        type,
        label,
        provider,
        urlIcon,
        providerIcon
      }) : providerIcon;
  }
};
export default extractJsonldDataIcon;

/**
 * Choose which icon to show when the type is not recognized.
 */
const extractDefaultIcon = ({
  data,
  type,
  label,
  provider,
  urlIcon,
  providerIcon
}) => {
  const documentData = data;
  const fileFormat = documentData === null || documentData === void 0 ? void 0 : documentData['schema:fileFormat'];
  const fileFormatIcon = extractFileFormatIcon(fileFormat);
  const documentTypeIcon = extractDocumentTypeIcon(type, label);
  return prioritiseIcon({
    providerId: provider,
    fileFormatIcon,
    documentTypeIcon,
    urlIcon,
    providerIcon
  });
};